import React, { useMemo } from 'react';
import { isEmpty, isNil, partial } from 'lodash';

import Box from 'components/Box';
import CheckboxInput from 'components/forms/CheckboxInput';
import Text from 'components/Text';

import AccessLevel from './AccessLevel/AccessLevel';
import ContactInfo from './ContactInfo/ContactInfo';
import PacketRow from './Onboarding/PacketRow';
import FormPayroll from './FormPayroll';
import RolesBlock from './RolesBlock';

const Form = ({
  isPayrollUpdating,
  user,
  handleChange,
  handleBlur,
  handlePacketChange,
  handleJobTaxClassificationChange,
  handleContractorTypeChange,
  fourTensRuleEnabled,
  setFieldValue,
  isAContractor,
  cxEl,
  canViewWages,
  isPayrollSetup,
  isPayrollEnrolled,
  hasOnboardingDocsAccess,
  newHireOnboardingActivated,
  numberOfDocumentsInContractorTemplate,
  numberOfDocumentsInEmployeeTemplate,
  firstName,
  lastName,
  phone,
  email,
  wageAttributes,
  hireDate,
  sendSms,
  jobTaxClassification,
  contractorType,
  packet,
  level,
  includedInPayroll,
  requestInformation,
  errors = {},
  canRequestInformation,
  isEligibleForPayroll,
  userId,
  firstNameMaximum,
  hiringSourceExperiment,
  canEditTaxClassification,
  invalidPayrollInfo,
  hasNoActivePayments,
  hasITIN,
  hasPaidPayrollRunsForJob,
  accessLevelOptions,
}) => {
  const canShowRequiredFieldError = isEligibleForPayroll
    ? false
    : includedInPayroll && isPayrollUpdating;

  const isEmployee = useMemo(
    () => jobTaxClassification !== '1099',
    [jobTaxClassification]
  );

  return isPayrollEnrolled ? (
    <FormPayroll
      hasITIN={hasITIN}
      hasNoActivePayments={hasNoActivePayments}
      hasPaidPayrollRunsForJob={hasPaidPayrollRunsForJob}
      user={user}
      invalidPayrollInfo={invalidPayrollInfo}
      isPayrollUpdating={isPayrollUpdating}
      handleChange={handleChange}
      handleBlur={handleBlur}
      handlePacketChange={handlePacketChange}
      handleJobTaxClassificationChange={handleJobTaxClassificationChange}
      jobTaxClassification={jobTaxClassification}
      handleContractorTypeChange={handleContractorTypeChange}
      contractorType={contractorType}
      fourTensRuleEnabled={fourTensRuleEnabled}
      canEditTaxClassification={canEditTaxClassification}
      setFieldValue={setFieldValue}
      isEmployee={isEmployee}
      isAContractor={isAContractor}
      cxEl={cxEl}
      canViewWages={canViewWages}
      isPayrollSetup={isPayrollSetup}
      hasOnboardingDocsAccess={hasOnboardingDocsAccess}
      numberOfDocumentsInContractorTemplate={
        numberOfDocumentsInContractorTemplate
      }
      numberOfDocumentsInEmployeeTemplate={numberOfDocumentsInEmployeeTemplate}
      firstName={firstName}
      lastName={lastName}
      phone={phone}
      email={email}
      wageAttributes={wageAttributes}
      hireDate={hireDate}
      sendSms={sendSms}
      packet={packet}
      level={level}
      includedInPayroll={includedInPayroll}
      requestInformation={requestInformation}
      errors={errors}
      canRequestInformation={canRequestInformation}
      isEligibleForPayroll={isEligibleForPayroll}
      userId={userId}
      firstNameMaximum={firstNameMaximum}
      hiringSourceExperiment={hiringSourceExperiment}
      isPayrollEnrolled={isPayrollEnrolled}
      accessLevelOptions={accessLevelOptions}
    />
  ) : (
    <Box>
      <ContactInfo
        handleChange={partial(handleChange)}
        handleBlur={handleBlur}
        firstName={firstName}
        lastName={lastName}
        phone={phone}
        email={email}
        requestInformation={requestInformation}
        errors={errors}
        user={user}
      />
      {!isPayrollSetup && (
        <Box mb={24}>
          <CheckboxInput
            name="sendSms"
            value={sendSms}
            label={<Text fs14 i18n="add_team.individual_drawer.send_sms" />}
            onChange={partial(handleChange, 'sendSms')}
          />
        </Box>
      )}
      <Box mb={24} spacebetween>
        <AccessLevel
          cxEl={cxEl}
          handleChange={handleChange}
          level={level}
          accessLevelOptions={accessLevelOptions}
        />
      </Box>
      {canViewWages && (
        <Box mb={24}>
          <RolesBlock
            wageErrorBorder={
              canShowRequiredFieldError && isNil(wageAttributes[0]?.wage_rate)
            }
            onChange={partial(handleChange, 'wageAttributes')}
            editingRolesData={wageAttributes}
            key={userId}
            isEmployee={isEmployee}
          />
        </Box>
      )}

      {hasOnboardingDocsAccess && (
        <>
          {newHireOnboardingActivated && (
            <PacketRow
              isPayrollUpdating={isPayrollUpdating}
              hasITIN={hasITIN}
              user={user}
              hasNoActivePayments={hasNoActivePayments}
              hasPaidPayrollRunsForJob={hasPaidPayrollRunsForJob}
              errorBorder={
                canShowRequiredFieldError ||
                (requestInformation === 'packet' &&
                  isEmpty(jobTaxClassification))
              }
              handleJobTaxClassificationChange={
                handleJobTaxClassificationChange
              }
              jobTaxClassification={jobTaxClassification}
              handleContractorTypeChange={handleContractorTypeChange}
              contractorType={contractorType}
              selectedPacket={packet}
              handlePacketChange={handlePacketChange}
              numberOfDocumentsInContractorTemplate={
                numberOfDocumentsInContractorTemplate || 0
              }
              numberOfDocumentsInEmployeeTemplate={
                numberOfDocumentsInEmployeeTemplate || 0
              }
              canEditTaxClassification={canEditTaxClassification}
            />
          )}
        </>
      )}
    </Box>
  );
};

export default Form;
